import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { ChatMessage } from "@probetalab/contracts";
import { get, run } from "./api.js";
import { errorMessage } from "./useQuery.js";

/**
 * Chat entre sucursales, client side (ADR 0028). One state for the floating bubble, its panel and
 * the Inicio preview, so there is a single chat UI: Inicio's «Abrir chat» opens the same panel.
 *
 * - Unread count: polled every 20 s while the tab is visible (stopped when hidden, refreshed on
 *   return); a rise while closed bumps the badge and pulses the bubble once.
 * - Open: messages polled every 6 s; whatever is shown is marked read (`chat.read` up to the newest).
 * - Open/closed and the draft survive navigation and closing, per browser session.
 */

const OPEN_KEY = "probeta.chat.open";
const DRAFT_KEY = "probeta.chat.draft";
export const UNREAD_POLL_MS = 20_000;
export const OPEN_POLL_MS = 6_000;

/** "9+" past nine; null when there is nothing to show. */
export function badgeText(n: number): string | null {
  if (n <= 0) return null;
  return n > 9 ? "9+" : String(n);
}

export function bubbleLabel(n: number): string {
  return n > 0 ? `Chat entre sucursales, ${n} sin leer` : "Chat entre sucursales";
}

const timeFmt = new Intl.DateTimeFormat("es-MX", { timeZone: "America/Mexico_City", hour: "2-digit", minute: "2-digit", hour12: true });
const dayFmt = new Intl.DateTimeFormat("es-MX", { timeZone: "America/Mexico_City", day: "numeric", month: "short" });
const labDay = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City" });

/** "10:14 a. m." today, "3 nov · 10:14 a. m." on another day (lab time). */
export function messageTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const t = timeFmt.format(d);
  return labDay.format(d) === labDay.format(now) ? t : `${dayFmt.format(d).replace(".", "")} · ${t}`;
}

/** Merge a fresh page into what is shown: by id, oldest first, the fresh copy wins (a hide). */
export function mergeMessages(current: ChatMessage[], fresh: ChatMessage[]): ChatMessage[] {
  const byId = new Map(current.map((m) => [m.id, m]));
  for (const m of fresh) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
}

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeSession(key: string, value: string | null) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* private mode: state lives in memory only */
  }
}

function useVisible(): boolean {
  const [visible, setVisible] = useState(() => typeof document === "undefined" || document.visibilityState !== "hidden");
  useEffect(() => {
    const on = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return visible;
}

export interface ChatState {
  enabled: boolean;
  open: boolean;
  setOpen: (v: boolean) => void;
  toggle: () => void;
  unread: number;
  pulse: boolean;
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  draft: string;
  setDraft: (v: string) => void;
  send: () => Promise<boolean>;
  sending: boolean;
  hide: (id: string) => Promise<void>;
  /** For Inicio's preview: refresh the latest messages without opening the panel. */
  refresh: () => Promise<void>;
}

const ChatContext = createContext<ChatState | null>(null);

export function ChatProvider({ enabled, branchId, children }: { enabled: boolean; branchId: string | null; children: ReactNode }) {
  const [open, setOpenState] = useState(() => enabled && readSession(OPEN_KEY) === "1");
  const [unread, setUnread] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraftState] = useState(() => readSession(DRAFT_KEY) ?? "");
  const [sending, setSending] = useState(false);
  const visible = useVisible();
  const lastUnread = useRef(0);
  const readUpTo = useRef<string | null>(null);

  const setOpen = useCallback((v: boolean) => {
    setOpenState(v);
    writeSession(OPEN_KEY, v ? "1" : null);
  }, []);
  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);
  const setDraft = useCallback((v: string) => {
    setDraftState(v);
    writeSession(DRAFT_KEY, v || null);
  }, []);

  const pollUnread = useCallback(async () => {
    try {
      const r = await get("chatUnread");
      if (r.count > lastUnread.current) {
        setPulse(true);
        window.setTimeout(() => setPulse(false), 2400);
      }
      lastUnread.current = r.count;
      setUnread(r.count);
    } catch {
      /* a failed poll keeps the last count; the next one retries */
    }
  }, []);

  const markRead = useCallback(async (list: ChatMessage[]) => {
    const newest = list[list.length - 1]?.created_at;
    if (!newest || (readUpTo.current && readUpTo.current >= newest)) return;
    readUpTo.current = newest;
    try {
      await run("chat.read", { up_to: newest }, branchId ? { branch_id: branchId } : {});
      lastUnread.current = 0;
      setUnread(0);
    } catch {
      readUpTo.current = null;
    }
  }, [branchId]);

  const loadMessages = useCallback(async (markAsRead: boolean) => {
    setLoading((l) => l || messages.length === 0);
    try {
      const v = await get("chat", { query: { limit: 50 } });
      setMessages((cur) => mergeMessages(cur, v.items));
      setError(null);
      if (markAsRead) await markRead(v.items);
      else {
        lastUnread.current = v.unread;
        setUnread(v.unread);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [markRead, messages.length]);

  // Unread polling while visible (and closed); messages polling while open.
  useEffect(() => {
    if (!enabled || !visible) return;
    if (open) {
      void loadMessages(true);
      const t = window.setInterval(() => void loadMessages(true), OPEN_POLL_MS);
      return () => window.clearInterval(t);
    }
    void pollUnread();
    const t = window.setInterval(() => void pollUnread(), UNREAD_POLL_MS);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, visible, open]);

  const send = useCallback(async (): Promise<boolean> => {
    const body = draft.trim();
    if (!body || sending) return false;
    setSending(true);
    try {
      const m = await run<"chat.message.send", ChatMessage>("chat.message.send", { body }, branchId ? { branch_id: branchId } : {});
      setMessages((cur) => mergeMessages(cur, [m]));
      readUpTo.current = m.created_at;
      setDraft("");
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setSending(false);
    }
  }, [draft, sending, branchId, setDraft]);

  const hide = useCallback(async (id: string) => {
    try {
      const m = await run<"chat.message.hide", ChatMessage>("chat.message.hide", { message_id: id }, branchId ? { branch_id: branchId } : {});
      setMessages((cur) => mergeMessages(cur, [m]));
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [branchId]);

  const refresh = useCallback(() => loadMessages(open), [loadMessages, open]);

  const value = useMemo<ChatState>(
    () => ({ enabled, open: enabled && open, setOpen, toggle, unread, pulse, messages, loading, error, draft, setDraft, send, sending, hide, refresh }),
    [enabled, open, setOpen, toggle, unread, pulse, messages, loading, error, draft, setDraft, send, sending, hide, refresh],
  );
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatState {
  const c = useContext(ChatContext);
  if (!c) throw new Error("useChat outside ChatProvider");
  return c;
}
